import type { Dinero } from 'dinero.js';
import mapValues from 'lodash/mapValues';
import type { DateTime } from 'luxon';
import type { PartialDeep } from 'type-fest';

/**
 * Like the built-in type `Omit`, but it separately omits the property from each of the component types in a
 * discriminated type union.
 */
export type ExcludeFromUnion<T, K extends PropertyKey> = T extends any ? Omit<T, K> : never;

export type ExcludeMethods<T> = Pick<
  T,
  {
    [Key in keyof T]: T[Key] extends Function ? never : Key;
  }[keyof T]
>;

/**
 * Types that should not be recursively traversed for performance.
 * These are "leaf" types that have complex internal structure we don't care about.
 *
 * Based on react-hook-form's BrowserNativeObject pattern.
 */
export type BrowserNativeObject =
  | Date
  | FileList
  | File
  | DateTime // Luxon DateTime - has deep internal zone/locale properties
  | Dinero // Dinero money objects - has deep internal precision properties
  | RegExp
  | Blob
  | Map<any, any>
  | Set<any>
  | WeakMap<any, any>
  | WeakSet<any>;

/**
 * Optimized ExcludeMethodsDeep with early termination for BrowserNativeObject.
 * Prevents unnecessary deep recursion into library types like DateTime and Dinero.
 */
export type ExcludeMethodsDeep<T> = T extends (infer Elem)[]
  ? ExcludeMethodsDeep<Elem>[]
  : T extends BrowserNativeObject
    ? T // Early termination - don't recurse into these types
    : T extends object
      ? {
          // Optimized: single pass through keys, filtering functions and transforming values
          [Key in keyof T as T[Key] extends Function ? never : Key]: ExcludeMethodsDeep<T[Key]>;
        }
      : T;

type stripNull<T> = T extends null ? never : T;

type stripNullDeep<T> = T extends (infer Elem)[]
  ? stripNullDeep<Elem>[]
  : T extends BrowserNativeObject
    ? T // Early termination
    : T extends object
      ? { [Key in keyof T]: stripNullDeep<T[Key]> }
      : stripNull<T>;

export function RemoveNullProperties<T extends Record<string, any>>(
  obj: T,
): PartialDeep<stripNullDeep<T>> {
  return mapValues(obj, (value) => {
    if (value === null || value === undefined) {
      return undefined;
    }
    if (Array.isArray(value)) {
      // Only try to remove null properties from objects (including arrays and class instances).
      return value.map((v: unknown) => (v instanceof Object ? RemoveNullProperties(v) : v));
    }
    if (typeof value === 'object') {
      return RemoveNullProperties(value);
    }
    return value;
  }) as PartialDeep<stripNullDeep<T>>;
}

// ============================================================================
// Path type implementation with circular reference detection
// Based on react-hook-form's pattern with additional React component handling
// ============================================================================

// See https://dev.to/pffigueiredo/typescript-utility-keyof-nested-object-2pa3#comment-1nm58
type FieldValues = Record<string, any>;

type Primitive = null | undefined | string | number | boolean | symbol | bigint;

type IsTuple<T extends ReadonlyArray<any>> = number extends T['length'] ? false : true;
type TupleKey<T extends ReadonlyArray<any>> = Exclude<keyof T, keyof any[]>;
type ArrayKey = number;

/**
 * Checks whether T1 can be exactly (mutually) assigned to T2.
 * Used for circular reference detection.
 * From react-hook-form.
 *
 * @example
 * IsEqual<string, string> = true
 * IsEqual<'foo', 'foo'> = true
 * IsEqual<string, number> = false
 * IsEqual<string, 'foo'> = false
 */
export type IsEqual<T1, T2> = T1 extends T2
  ? (<G>() => G extends T1 ? 1 : 2) extends <G>() => G extends T2 ? 1 : 2
    ? true
    : false
  : false;

/**
 * Helper to check if any part of a union T1 is equal to T2.
 * Used in circular reference detection.
 * From react-hook-form.
 */
type AnyIsEqual<T1, T2> = T1 extends T2 ? (IsEqual<T1, T2> extends true ? true : never) : never;

/**
 * Helper type for recursively constructing paths through a type.
 *
 * Key optimizations:
 * 1. Early termination for Primitive and BrowserNativeObject types
 * 2. Function filtering to exclude methods from classes
 * 3. Structural checks for DateTime, Duration, and Dinero to prevent enumerating internal properties
 * 4. Circular reference detection using TraversedTypes accumulator
 * 5. Depth limit of 5 levels to prevent excessive nesting
 *
 * Note: DateTime, Duration, and Dinero get special structural checks before the BrowserNativeObject
 * extends clause. This is critical because:
 * - Structural checks (V extends { isValid: ...; zone: ...; toISO: ... }) are fast
 * - They stop path enumeration (e.g., 'startTime' only, not 'startTime.day' | 'startTime.month' | ...)
 * - They execute before TypeScript evaluates the complex extends BrowserNativeObject check
 * - This prevents both complexity issues AND unwanted path pollution
 *
 * @typeParam K - current key being processed
 * @typeParam V - value type at current key
 * @typeParam TraversedTypes - union of all types traversed so far (for circular detection)
 * @typeParam Depth - current depth level (max 5)
 */
type PathImpl<
  K extends string | number,
  V,
  TraversedTypes,
  Depth extends number = 5,
> = Depth extends 0
  ? never // Max depth reached - no deeper paths allowed
  : V extends Primitive | BrowserNativeObject
    ? `${K}`
    : V extends Function
      ? never
      : // Structural check for DateTime (has isValid, zone, and toISO properties)
        V extends { isValid: boolean; zone: any; toISO: any }
        ? `${K}`
        : // Structural check for Duration (has years, months, isValid properties)
          V extends { years: number; months: number; isValid: boolean }
          ? `${K}`
          : // Structural check for Dinero (has getAmount, getCurrency methods)
            V extends { getAmount: any; getCurrency: any; add: any }
            ? `${K}`
            : // Check so that we don't recurse into the same type
              // by ensuring that the types are mutually assignable
              // mutually required to avoid false positives of subtypes
              true extends AnyIsEqual<TraversedTypes, V>
              ? `${K}`
              : `${K}` | `${K}.${PathInternal<V, TraversedTypes | V, Decrement<Depth>>}`;

// Helper type to decrement depth
type Decrement<N extends number> = N extends 5
  ? 4
  : N extends 4
    ? 3
    : N extends 3
      ? 2
      : N extends 2
        ? 1
        : N extends 1
          ? 0
          : never;

/**
 * Internal implementation of Path with traversal tracking.
 * This is separated from the public API to hide the TraversedTypes and Depth implementation details.
 *
 * @typeParam T - type to extract paths from
 * @typeParam TraversedTypes - union of types already traversed (starts with T itself)
 * @typeParam Depth - current depth level (starts at 5)
 */
type PathInternal<T, TraversedTypes = T, Depth extends number = 5> =
  T extends ReadonlyArray<infer V>
    ? IsTuple<T> extends true
      ? {
          [K in TupleKey<T>]-?: PathImpl<K & string, T[K], TraversedTypes, Depth>;
        }[TupleKey<T>]
      : PathImpl<ArrayKey, V, TraversedTypes, Depth>
    : {
        [K in keyof T]-?: PathImpl<K & string, T[K], TraversedTypes, Depth>;
      }[keyof T];

/**
 * Type representing all possible dot-notation paths through a type T.
 *
 * PERFORMANCE OPTIMIZATIONS AND DESIGN DECISIONS:
 * - Circular reference detection prevents infinite recursion (fixes TS2589)
 * - Function filtering to exclude methods from class instances
 * - Structural checks for DateTime/Duration/Dinero stop path expansion at these types
 * - TraversedTypes accumulator tracks visited types
 *
 * LUXON/DINERO LIBRARY TYPE HANDLING:
 * Properties of type DateTime, Duration, or Dinero (like `startTime: DateTime`, `interval: Duration`)
 * will only generate the direct path (e.g., 'startTime', 'interval'), NOT their internal properties.
 * This is intentional to:
 * 1. Avoid overwhelming path unions with thousands of internal library properties
 * 2. Prevent TypeScript from hitting complexity limits
 * 3. Keep path suggestions relevant to actual data properties
 *
 * @example
 * ```typescript
 * type Example = {
 *   foo: { bar: string },
 *   timestamp: DateTime,
 *   duration: Duration
 * };
 * type Paths = Path<Example>;
 * // Result: 'foo' | 'foo.bar' | 'timestamp' | 'duration'
 * // NOT: 'foo' | 'foo.bar' | 'timestamp' | 'timestamp.day' | 'duration.hours' | ...
 * ```
 */
export type Path<T> = T extends any ? PathInternal<T> : never;

export type NestedKeyOf<TFieldValues extends FieldValues> = Path<TFieldValues>;

/**
 * Helper type for ArrayPath implementation.
 * Similar to PathImpl but only includes paths that point to arrays.
 * Uses the same structural checks and depth limiting as PathImpl.
 */
type ArrayPathImpl<
  K extends string | number,
  V,
  TraversedTypes,
  Depth extends number = 5,
> = Depth extends 0
  ? never // Max depth reached
  : V extends Primitive | BrowserNativeObject
    ? never // Primitives and BrowserNativeObjects are not arrays
    : // Structural check for DateTime
      V extends { isValid: boolean; zone: any; toISO: any }
      ? never // DateTime is not an array
      : // Structural check for Duration
        V extends { years: number; months: number; isValid: boolean }
        ? never // Duration is not an array
        : // Structural check for Dinero
          V extends { getAmount: any; getCurrency: any; add: any }
          ? never // Dinero is not an array
          : V extends ReadonlyArray<infer U>
            ? U extends Primitive | BrowserNativeObject
              ? never // Arrays of primitives/BrowserNativeObjects - don't recurse further
              : U extends { isValid: boolean; zone: any; toISO: any }
                ? never // Arrays of DateTime - don't recurse further
                : U extends { years: number; months: number; isValid: boolean }
                  ? never // Arrays of Duration - don't recurse further
                  : U extends { getAmount: any; getCurrency: any; add: any }
                    ? never // Arrays of Dinero - don't recurse further
                    : true extends AnyIsEqual<TraversedTypes, V>
                      ? never // Circular reference - stop
                      :
                          | `${K}`
                          | `${K}.${ArrayPathInternal<V, TraversedTypes | V, Decrement<Depth>>}`
            : true extends AnyIsEqual<TraversedTypes, V>
              ? never // Circular reference - stop
              : `${K}.${ArrayPathInternal<V, TraversedTypes | V, Decrement<Depth>>}`;

/**
 * Internal implementation of ArrayPath with traversal tracking.
 * Includes depth limiting like PathInternal.
 */
type ArrayPathInternal<T, TraversedTypes = T, Depth extends number = 5> =
  T extends ReadonlyArray<infer V>
    ? IsTuple<T> extends true
      ? {
          [K in TupleKey<T>]-?: ArrayPathImpl<K & string, T[K], TraversedTypes, Depth>;
        }[TupleKey<T>]
      : ArrayPathImpl<ArrayKey, V, TraversedTypes, Depth>
    : {
        [K in keyof T]-?: ArrayPathImpl<K & string, T[K], TraversedTypes, Depth>;
      }[keyof T];

/**
 * Type representing all possible paths through a type that point to array values.
 *
 * @example
 * ```typescript
 * type Example = { items: Array<{ id: number }>, name: string };
 * type Paths = ArrayPath<Example>;
 * // Result: 'items' | 'items.0' (but not 'name' or 'items.0.id')
 * ```
 */
export type ArrayPath<T> = T extends any ? ArrayPathInternal<T> : never;

/**
 * Helper type for PathValue implementation with circular detection.
 *
 * @typeParam T - type being indexed
 * @typeParam P - path string through the type
 * @typeParam TraversedTypes - types already traversed (for circular detection)
 */
type PathValueImpl<T, P extends string, TraversedTypes = T> = T extends any
  ? P extends `${infer K}.${infer R}`
    ? // Nested path: recurse into the nested type
      K extends keyof T
      ? true extends AnyIsEqual<TraversedTypes, T[K]>
        ? T[K] // Circular reference detected - return type without recursing
        : undefined extends T[K]
          ? PathValueImpl<T[K], R, TraversedTypes | T[K]> | undefined
          : PathValueImpl<T[K], R, TraversedTypes | T[K]>
      : K extends `${ArrayKey}`
        ? T extends ReadonlyArray<infer V>
          ? PathValueImpl<V, R, TraversedTypes | V>
          : never
        : never
    : // Direct property access
      P extends keyof T
      ? T[P]
      : P extends `${ArrayKey}`
        ? T extends ReadonlyArray<infer V>
          ? V
          : never
        : never
  : never;

/**
 * Type representing the value type at a given path through a type.
 * Now includes circular reference detection for reliability.
 *
 * Note: The constraint on P is relaxed to `string` for performance.
 * Path validation happens at runtime via lodash's getIn/setIn.
 *
 * @example
 * ```typescript
 * type Example = { foo: { bar: string } };
 * type Value = PathValue<Example, 'foo.bar'>; // string
 * ```
 */
export type PathValue<T, P extends string> = P extends Path<T> | ArrayPath<T>
  ? PathValueImpl<T, P>
  : any;

export type NestedKeyOfValue<
  TFieldValues extends FieldValues,
  TFieldPath extends NestedKeyOf<TFieldValues> = NestedKeyOf<TFieldValues>,
> = PathValue<TFieldValues, TFieldPath>;

export type KeysOfType<T, ValueType> = {
  [K in keyof T]: T[K] extends ValueType ? K : never;
}[Extract<keyof T, string>];
